import dayjs from "dayjs";

import { ListViewType } from "@/constants";
import { TimePoints } from "@/constants/timeRange";
import { FlightItem } from "@/typings";

import { FilterData, FilterSourceData } from "../typing";
import { AircraftUnknown, MixedAirlineKey } from "../utils/constants";

const compareRangeTime = (time: string, range: number[]) => {
  const rangeTime = [TimePoints[range[0]], TimePoints[range[1]]];

  return time >= rangeTime[0] && time <= rangeTime[1];
};

const programsFilterFn = (data: FlightItem, value: FilterData["programs"]): boolean => {
  if (value.length === 0) {
    return true;
  }
  return value.includes(data.program_code);
};

const stopsFilterFn = (data: FlightItem, value: FilterData["stops"]): boolean => {
  if (value === "-1") {
    return true;
  }
  return `${data.fare.products.length - 1}` <= value;
};

const airlinesFilterFn = (data: FlightItem, value: FilterData["airlines"]): boolean => {
  if (value.length === 0) {
    return true;
  }

  // 特殊逻辑, useFilterOptions\useFilterOptionsCount\useFilterList需要同步修改
  let airlineName = data.airline_name.trim();
  if (airlineName.includes("&")) {
    airlineName = MixedAirlineKey;
  }
  return value.includes(airlineName);
};

const banksFilterFn = (data: FlightItem, value: FilterData["banks"]): boolean => {
  if (value.length === 0) {
    return true;
  }
  return data.transfer_options?.some(({ program }) => value.includes(program));
};

const maxPointsFilterFn = (data: FlightItem, value: FilterData["maxPoints"]): boolean => {
  if (value === -1) {
    return true;
  }
  return data.award_points <= value;
};

const taxFilterFn = (data: FlightItem, value: FilterData["tax"]): boolean => {
  if (value === -1) {
    return true;
  }
  return data.surcharge <= value;
};

const premiumFilterFn = (data: FlightItem, value: FilterData["premium"]): boolean => {
  if (value.length === 0) {
    return true;
  }

  return (data.percent_premium >= value[0] && data.percent_premium <= value[1]) && (data.final_cabin_type?.toLowerCase() == "first" || data.final_cabin_type?.toLowerCase() == "business");
};

const durationFilterFn = (data: FlightItem, value: FilterData["duration"]): boolean => {
  if (value === -1) {
    return true;
  }
  return data.fare.travel_minutes_total <= value;
};

const airportFilterFn = (data: FlightItem, value: FilterData["airport"]): boolean => {
  if (!value) {
    return true;
  }
  if (value.length === 0) {
    return true;
  }
  return data.fare.products.every((item) => {
    return value.includes(item.origin) && value.includes(item.destination);
  });
};

const airportOutboundFilterFn = (
  data: FlightItem,
  value: FilterData["airportOutbound"]
): boolean => {
  if (value.length === 0) {
    return true;
  }

  return value.includes(data.fare.products[0].origin);
};

const airportInboundFilterFn = (data: FlightItem, value: FilterData["airportInbound"]): boolean => {
  if (value.length === 0) {
    return true;
  }

  return value.includes(data.fare.products[data.fare.products.length - 1].destination);
};

const connectingAirportsFilterFn = (
  data: FlightItem,
  value: FilterData["connectingAirports"]
): boolean => {
  if (!value) {
    return true;
  }
  if (value.length === 0) {
    return true;
  }
  const connectinAirportOptions = new Set<string>();
  if (data.fare.products.length > 1) {
    const products = data.fare.products;
    const first_destination = products[0].destination;
    connectinAirportOptions.add(first_destination);
    const last_origin = products[products.length - 1].origin;
    connectinAirportOptions.add(last_origin);

    const connectingSegments = products.slice(1, products.length - 1);
    connectingSegments.forEach((item) => {
      const destination = item.destination;
      connectinAirportOptions.add(destination);
      const origin = item.origin;
      connectinAirportOptions.add(origin);
    });

    return Array.from(connectinAirportOptions).every((item) => value.includes(item));
  }

  return true;
};

const aircraftsFilterFn = (data: FlightItem, value: FilterData["aircrafts"]): boolean => {
  if (value.length === 0) {
    return true;
  }
  return data.fare.products.every((item) => {
    const aircraft = item.aircraft || AircraftUnknown;
    return value.includes(aircraft);
  });
};

const connectionTimeFilterFn = (data: FlightItem, value: FilterData["connectionTime"]): boolean => {
  if (!value.length) {
    return true;
  }

  const total = data.fare.products.reduce((a, b) => a + b.layover_time, 0);
  return total >= value[0] && total <= value[1];
};

const departureTimeFilterFn = (data: FlightItem, value: FilterData["departureTime"]): boolean => {
  const d = dayjs(data.fare.products[0].departure_time).format("HH:mm");

  return compareRangeTime(d, value);
};

const arrivalTimeFilterFn = (data: FlightItem, value: FilterData["arrivalTime"]): boolean => {
  const d = dayjs(data.fare.products[data.fare.products.length - 1].arrival_time).format("HH:mm");

  return compareRangeTime(d, value);
};

const dateFilterFn = (data: FlightItem, value: FilterData["date"]): boolean => {
  if (value.length === 0) {
    return true;
  }
  return value.includes(data.date);
};

const cabinFilterFn = (data: FlightItem, value: FilterData["cabins"]): boolean => {
  if (value.length === 0) {
    return true;
  }
  return value.includes(data.payment?.cabin || data.final_cabin_type);
};

const maxPointsEconomyFilterFn = (
  data: FlightItem,
  value: FilterData["gridModeMaxPoints"]
): boolean => {
  if (value[0] === -1) {
    return true;
  }
  return data.cabin_prices.Economy?.miles > 0 && value[0] >= data.cabin_prices.Economy?.miles;
};
const maxPointsPremiumEconomyFilterFn = (
  data: FlightItem,
  value: FilterData["gridModeMaxPoints"]
): boolean => {
  if (value[1] === -1) {
    return true;
  }

  return (
    data.cabin_prices["Premium Economy"]?.miles > 0 &&
    value[1] >= data.cabin_prices["Premium Economy"]?.miles
  );
};
const maxPointsFirstFilterFn = (
  data: FlightItem,
  value: FilterData["gridModeMaxPoints"]
): boolean => {
  if (value[2] === -1) {
    return true;
  }
  return data.cabin_prices.First?.miles > 0 && value[2] >= data.cabin_prices.First?.miles;
};
const maxPointsBusinessFilterFn = (
  data: FlightItem,
  value: FilterData["gridModeMaxPoints"]
): boolean => {
  if (value[3] === -1) {
    return true;
  }
  return data.cabin_prices.Business?.miles > 0 && value[3] >= data.cabin_prices.Business?.miles;
};

const taxEconomyFilterFn = (data: FlightItem, value: FilterData["gridModeTax"]): boolean => {
  if (value[0] === -1) {
    return true;
  }
  return data.cabin_prices.Economy?.tax > 0 && value[0] >= data.cabin_prices.Economy?.tax;
};
const taxPremiumEconomyFilterFn = (data: FlightItem, value: FilterData["gridModeTax"]): boolean => {
  if (value[1] === -1) {
    return true;
  }

  return (
    data.cabin_prices["Premium Economy"]?.tax > 0 &&
    value[1] >= data.cabin_prices["Premium Economy"]?.tax
  );
};
const taxFirstFilterFn = (data: FlightItem, value: FilterData["gridModeTax"]): boolean => {
  if (value[2] === -1) {
    return true;
  }
  return data.cabin_prices.First?.tax > 0 && value[2] >= data.cabin_prices.First?.tax;
};
const taxBusinessFilterFn = (data: FlightItem, value: FilterData["gridModeTax"]): boolean => {
  if (value[3] === -1) {
    return true;
  }
  return data.cabin_prices.Business?.tax > 0 && value[3] >= data.cabin_prices.Business?.tax;
};

const premiumFirstFilterFn = (data: FlightItem, value: FilterData["gridModePremium"]): boolean => {
  if (value[0] === 0) {
    return true;
  }
  return (
    data.cabin_prices.First?.premium_cabin_percentage > 0 &&
    value[0] <= data.cabin_prices.First?.premium_cabin_percentage
  );
};
const premiumBusinessFilterFn = (
  data: FlightItem,
  value: FilterData["gridModePremium"]
): boolean => {
  if (value[1] === 0) {
    return true;
  }
  return (
    data.cabin_prices.Business?.premium_cabin_percentage > 0 &&
    value[1] <= data.cabin_prices.Business?.premium_cabin_percentage
  );
};

const advisoryFilterFn = (data: FlightItem, options: FilterData["advisory"]): boolean => {
  if (options.length === 0) {
    return true;
  }

  if (!data.advisory?.length) {
    return true;
  }

  const optionsSet = new Set(options);
  // console.log(optionsSet, data.advisory)
  // if (data.advisory.every(item => optionsSet.has(item))) {
  //   console.log("Value:", options, data.advisory, data.advisory.every(item => optionsSet.has(item)), data)
  // } else {
  //   console.log("HEY")
  // }
  return data.advisory.every((item) => optionsSet.has(item));
};

const gridModeCabinFilterFn = (data: FlightItem, value: FilterData["gridModeCabins"]): boolean => {
  if (value.length === 0) {
    return true;
  }
  return value.every((key) => Object.keys(data.cabin_prices).includes(key));
};

/**
 * 根据过滤条件筛选数据源
 * @param list
 * @param filter
 * @returns
 */
export const useFilterList = (
  list: FilterSourceData,
  filter: FilterData,
  listViewType: number
): FilterSourceData => {
  const isGridMode = listViewType === ListViewType.Grid;

  return list
    .filter((item) => {
      return [
        programsFilterFn(item, filter["programs"]),
        banksFilterFn(item, filter["banks"]),
        stopsFilterFn(item, filter["stops"]),
        airlinesFilterFn(item, filter["airlines"]),
        maxPointsFilterFn(item, filter["maxPoints"]),
        taxFilterFn(item, filter["tax"]),

        durationFilterFn(item, filter["duration"]),
        airportOutboundFilterFn(item, filter["airportOutbound"]),
        airportInboundFilterFn(item, filter["airportInbound"]),
        connectingAirportsFilterFn(item, filter["connectingAirports"]),
        airportFilterFn(item, filter["airport"]),
        aircraftsFilterFn(item, filter["aircrafts"]),
        connectionTimeFilterFn(item, filter["connectionTime"]),
        departureTimeFilterFn(item, [filter["times"][0], filter["times"][1]]),
        arrivalTimeFilterFn(item, [filter["times"][2], filter["times"][3]]),
        dateFilterFn(item, filter["date"]),
        cabinFilterFn(item, filter["cabins"]),
        advisoryFilterFn(item, filter["advisory"]),

        !isGridMode ? premiumFilterFn(item, filter["premium"]) : true,

        isGridMode ? maxPointsEconomyFilterFn(item, filter["gridModeMaxPoints"]) : true,
        isGridMode ? maxPointsPremiumEconomyFilterFn(item, filter["gridModeMaxPoints"]) : true,
        isGridMode ? maxPointsFirstFilterFn(item, filter["gridModeMaxPoints"]) : true,
        isGridMode ? maxPointsBusinessFilterFn(item, filter["gridModeMaxPoints"]) : true,

        isGridMode ? taxEconomyFilterFn(item, filter["gridModeTax"]) : true,
        isGridMode ? taxPremiumEconomyFilterFn(item, filter["gridModeTax"]) : true,
        isGridMode ? taxFirstFilterFn(item, filter["gridModeTax"]) : true,
        isGridMode ? taxBusinessFilterFn(item, filter["gridModeTax"]) : true,

        isGridMode ? premiumFirstFilterFn(item, filter["gridModePremium"]) : true,
        isGridMode ? premiumBusinessFilterFn(item, filter["gridModePremium"]) : true,
        isGridMode ? gridModeCabinFilterFn(item, filter["gridModeCabins"]) : true,
      ].every(Boolean);
    })
    .map((item) => {
      return {
        ...item,
        transfer_options: !filter.banks.length
          ? item.transfer_options
          : item.transfer_options?.filter(({ program }) => {
              return filter.banks.includes(program);
            }),
        cabin_prices: !filter.banks.length
          ? item.cabin_prices
          : Object.fromEntries(
              Object.entries(item.cabin_prices ? item.cabin_prices : {}).map(([key, value]) => [
                key,
                {
                  ...value,
                  transfer_options: value.transfer_options?.filter(({ program }) =>
                    filter.banks.includes(program)
                  ),
                },
              ])
            ),
      };
    });
};
